import React, { FC, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { PDFDocumentProxy } from 'pdfjs-dist';
import { FaSearchMinus, FaSearchPlus } from 'react-icons/fa';
import { FiRotateCcw, FiRotateCw } from 'react-icons/fi';
import { LoadingBlock } from '../LoadingBlock';
import './DocumentViewer.scss';
import { Nullable } from '../../types/custom/Nullable';

const minimumZoom = .25;

interface Props {
    showModal: boolean,
    blob: Blob,
    fileName: string,
    toggleModal: () => void
}

const DocumentViewer: FC<Props> = ({ showModal, blob, fileName, toggleModal }) => {
    const [ currentDocNumPages, setCurrentDocNumPages ] = useState<Nullable<number>>(null);
    const [ currentDocumentPageNumber, setCurrentDocumentPageNumber ] = useState(1);
    const [ zoom, setZoom ] = useState(1);
    const [ rotate, setRotate ] = useState(0);
    const [ disableToolbar, setDisableToolbar ] = useState(false);

    const documentContainer = useRef<HTMLDivElement>(null);

    const onDocumentLoadSuccess = (pdf: PDFDocumentProxy) => {
        setCurrentDocNumPages(pdf.numPages);
        setDisableToolbar(true);
        setCurrentDocumentPageNumber(1);
        setZoom(1);
        setRotate(0);
    };

    const prevPage = () => {
        let pageNumber = currentDocumentPageNumber - 1;
        if(pageNumber < 1 && currentDocNumPages !== null) {
            pageNumber = currentDocNumPages;
        }

        setCurrentDocumentPageNumber(pageNumber);
        setDisableToolbar(true);

        documentContainer.current?.scrollTo(0, 0);
    };

    const nextPage = () => {
        let pageNumber = currentDocumentPageNumber + 1;
        if(currentDocNumPages !== null && pageNumber > currentDocNumPages){
            pageNumber = 1;
        }

        setCurrentDocumentPageNumber(pageNumber);
        setDisableToolbar(true);
            
        documentContainer.current?.scrollTo(0, 0);
    };

    const zoomIn = () => {
        const newZoom = zoom + .25;
        setZoom(newZoom);
        setDisableToolbar(true);
    };

    const zoomOut = () => {
        let newDisableToolBar = true;
        let newZoom  = zoom - .25;
        
        if(newZoom < minimumZoom){
            newZoom = minimumZoom;
            newDisableToolBar = false;
        }

        setZoom(newZoom);
        setDisableToolbar(newDisableToolBar);
    };

    const rotateRight = () => {
        let newRotate = rotate;
        newRotate += 90;

        if(newRotate > 270){
            newRotate = 0;
        }

        setRotate(newRotate);
        setDisableToolbar(true);
    };
    
    const rotateLeft = () => {
        let newRotate = rotate;
        newRotate -= 90;

        if(newRotate < 0){
            newRotate = 270;
        }

        setRotate(newRotate);
        setDisableToolbar(true);
    };

    const enableToolbar = () => {
        setDisableToolbar(false);
    };

    const style = { display: showModal ? 'block' : 'none'};
    const classes = `modal fade${showModal ? ' show' : ''}`;

    return (
        <React.Fragment>
            <div className={classes} style={style} id="noteModal" tabIndex={-1} role="dialog" aria-labelledby="documentModal" aria-hidden={showModal}>
                <div className="modal-dialog modal-dialog-centered" role="document" style={{maxWidth: "1000px", maxHeight: "calc(90% - (1.75rem * 2))"}}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="documentModal">Viewing: {fileName}</h5>
                            <button type="button" className="close" onClick={toggleModal} aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="d-flex flex-row-reverse mb-2">
                                <div className="btn-group">
                                    <button disabled={disableToolbar} type="button" className="btn btn-primary" onClick={zoomOut}><FaSearchMinus /></button>
                                    <button disabled={disableToolbar} type="button" className="btn btn-primary" onClick={zoomIn}><FaSearchPlus /></button>
                                    <button disabled={disableToolbar} type="button" className="btn btn-warning" onClick={rotateLeft}><FiRotateCcw /></button>
                                    <button disabled={disableToolbar} type="button" className="btn btn-warning" onClick={rotateRight}><FiRotateCw /></button>
                                </div>
                            </div>
                            <div className="document-container" ref={documentContainer}>
                                <Document
                                    file={blob}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    loading={<LoadingBlock />}
                                >
                                    <Page
                                        onRenderSuccess={enableToolbar}
                                        pageNumber={currentDocumentPageNumber} 
                                        scale={zoom}
                                        rotate={rotate}    
                                        loading={<LoadingBlock />}
                                    />
                                </Document>
                            </div>
                            <p className="text-center">
                                <button type="button" className="btn btn-link btn-sm" onClick={prevPage}>&#60;&#60;</button>
                                Page {currentDocumentPageNumber} of {currentDocNumPages}
                                <button type="button" className="btn btn-link btn-sm" onClick={nextPage}>&#62;&#62;</button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            {showModal && <div className="modal-backdrop fade show"></div>}
        </React.Fragment>
    );  
};

export default DocumentViewer;